import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  COPY_DASHBOARD_TO,
  GET_COMPANY_MEMBERS,
  Modal,
  SET_DASHBOARD_COLLABORATORS,
} from '@employ-engage/common-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Member = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

export interface DashboardShareModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  dashboard: {
    id: string;
    name: string;
    ownerUserId: string;
    collaboratorUserIds: string[];
  } | null;
  onShared: () => void;
}

const nameOf = (member: Member) =>
  `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() ||
  member.email ||
  member.id;

/**
 * Two genuinely different things, which is why this asks rather than assumes.
 *
 * A collaborator edits the same dashboard: both people see each other's
 * changes, for better and worse. A copy is a snapshot owned by the recipient
 * and independent from that moment. Picking one for the sender would be
 * picking wrong half the time.
 */
export function DashboardShareModal({
  open,
  setOpen,
  dashboard,
  onShared,
}: DashboardShareModalProps) {
  const { t } = useTranslation(['common']);
  const [mode, setMode] = useState<'collaborate' | 'copy'>('collaborate');
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data } = useQuery(GET_COMPANY_MEMBERS, { skip: !open });
  const [setCollaborators] = useMutation(SET_DASHBOARD_COLLABORATORS);
  const [copyTo] = useMutation(COPY_DASHBOARD_TO);

  // Reopening should show what is currently true, not what was left selected
  // last time.
  useEffect(() => {
    if (!open) return;
    setSelected(dashboard?.collaboratorUserIds ?? []);
    setMode('collaborate');
    setError(null);
  }, [open, dashboard]);

  const members: Member[] = (data?.companyMembers ?? []).filter(
    (member: Member) => member.id !== dashboard?.ownerUserId
  );

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id)
        ? current.filter((existing) => existing !== id)
        : [...current, id]
    );

  const submit = async () => {
    if (!dashboard) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'collaborate') {
        await setCollaborators({
          variables: { id: dashboard.id, userIds: selected },
        });
      } else {
        // One copy each, owned by the recipient. Sequential rather than
        // parallel so a failure part-way names the person it failed on.
        for (const userId of selected) {
          await copyTo({ variables: { id: dashboard.id, userId } });
        }
      }
      onShared();
      setOpen(false);
    } catch (err: any) {
      setError(err?.message ?? t('That did not work.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="space-y-4">
        <h2 className="text-base font-semibold">
          {t('Share')} “{dashboard?.name}”
        </h2>

        <div className="space-y-2">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              checked={mode === 'collaborate'}
              onChange={() => setMode('collaborate')}
              className="mt-1"
            />
            <span>
              <span className="font-medium">{t('Work on it together')}</span>
              <span className="block text-light-secondaryText">
                {t(
                  'One dashboard. Everyone sees each other’s changes as they happen.'
                )}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              checked={mode === 'copy'}
              onChange={() => setMode('copy')}
              className="mt-1"
            />
            <span>
              <span className="font-medium">{t('Send a copy')}</span>
              <span className="block text-light-secondaryText">
                {t(
                  'They get their own, to change however they like. Yours stays as it is.'
                )}
              </span>
            </span>
          </label>
        </div>

        <div className="max-h-56 overflow-y-auto rounded-md border border-gray-200 p-2">
          {members.length === 0 ? (
            <p className="p-2 text-sm text-light-secondaryText">
              {t('Nobody else is in this company yet.')}
            </p>
          ) : (
            members.map((member) => (
              <label
                key={member.id}
                className="flex items-center gap-2 py-1 text-sm"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(member.id)}
                  onChange={() => toggle(member.id)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                />
                {nameOf(member)}
              </label>
            ))
          )}
        </div>

        {mode === 'collaborate' && (
          <p className="text-xs text-light-secondaryText">
            {t('Unticking somebody removes their access. They keep nothing.')}
          </p>
        )}

        {error && (
          <p className="text-sm text-red-500" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={busy || (mode === 'copy' && selected.length === 0)}
            onClick={submit}
          >
            {mode === 'copy' ? t('Send copy') : t('Save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default DashboardShareModal;
